import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, defer, finalize, firstValueFrom, from, map, Observable, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { UserService } from './user.service';
import { RealtimeService } from './realtime.service';

@Injectable({ providedIn: 'root' })
export class SessionService {

    // Single-flight refresh (queues all 401s behind one refresh)
    private refreshInFlight$?: Observable<void>;

    // Optional: if you really want to dedupe login/logout clicks too
    private loginInFlight$?: Observable<void>;
    private logoutInFlight$?: Observable<void>;

    constructor(
        private auth: AuthService,
        private user: UserService,
        private realtime: RealtimeService,
        private router: Router
    ) { }

    login(email: string, password: string): Observable<void> {
        if (!this.loginInFlight$) {
            this.loginInFlight$ = this.auth.login(email, password).pipe(
                tap(res => {
                    this.user.restore(res);
                    this.realtime.start();
                }),
                switchMap(() => from(this.router.navigate(['/app/overview']))),
                map(() => void 0),
                shareReplay(1),
                finalize(() => (this.loginInFlight$ = undefined))
            );
        }
        return this.loginInFlight$;
    }

    externalLogin(provider: string): Observable<void> {
        // treat same as login (optional single-flight)
        if (!this.loginInFlight$) {
            this.loginInFlight$ = this.auth.externalLogin(provider).pipe(
                tap(res => {
                    this.user.restore(res);
                    this.realtime.start();
                }),
                switchMap(() => from(this.router.navigate(['/app/overview']))),
                map(() => void 0),
                shareReplay(1),
                finalize(() => (this.loginInFlight$ = undefined))
            );
        }
        return this.loginInFlight$;
    }

    refreshToken(): Observable<void> {
        if (!this.refreshInFlight$) {
            this.refreshInFlight$ = defer(() => {
                const currentToken = this.user.accessToken();
                if (!currentToken) return throwError(() => new Error('No access token available for refresh'));
                return this.auth.refresh(currentToken);
            }).pipe(
                tap(res => {
                    this.user.setAccessToken(res.accessToken, res.expiresAt);
                    this.realtime.refreshConnection();
                }),
                map(() => void 0),
                shareReplay(1),
                finalize(() => (this.refreshInFlight$ = undefined))
            );
        }
        return this.refreshInFlight$;
    }


    logout(): Observable<void> {
        if (!this.logoutInFlight$) {
            this.logoutInFlight$ = this.auth.logout().pipe(
                catchError(err => {
                    // still perform local cleanup + navigation
                    console.error(err);
                    return from([null]);
                }),
                tap(() => {
                    this.realtime.stop();
                    this.user.remove();
                }),
                switchMap(() => from(this.router.navigate(['/auth/login']))),
                map(() => void 0),
                shareReplay(1),
                finalize(() => (this.logoutInFlight$ = undefined))
            );
        }
        return this.logoutInFlight$;
    }

    logoutAll(): Observable<void> {
        // no need to single-flight separately; reuse logoutInFlight$ if you want
        if (!this.logoutInFlight$) {
            this.logoutInFlight$ = this.auth.logoutAll().pipe(
                catchError(err => {
                    console.error(err);
                    return from([null]);
                }),
                tap(() => {
                    this.realtime.stop();
                    this.user.remove();
                }),
                switchMap(() => from(this.router.navigate(['/auth/login']))),
                map(() => void 0),
                shareReplay(1),
                finalize(() => (this.logoutInFlight$ = undefined))
            );
        }
        return this.logoutInFlight$;
    }


    switchOrganization(orgId: string): Observable<void> {
        return this.auth.switchOrganization(orgId).pipe(
            tap(res => {
                // res must contain the new JWT with orgId claim
                this.user.restore(res);
                this.realtime.refreshConnection();
            }),
            switchMap(() => from(this.router.navigate(['/app']))),
            map(() => void 0)
        );
    }
}
